/**
 * Utility functions for managing push notification subscriptions
 */
import { apiClient } from '../../api/client';

/**
 * Check if push notifications are supported in the current browser
 */
export const isPushNotificationSupported = (): boolean => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

/**
 * Check if notifications are permitted by the user
 */
export const checkNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    return 'denied';
  }
  
  // If permission is already granted or denied, return the current status
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }
  
  // Otherwise, we need to ask for permission
  return 'default';
};

/**
 * Request permission to show notifications
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    console.warn('Notifications not supported');
    return 'denied';
  }
  
  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
};

/**
 * Subscribe to push notifications
 */
export const subscribeToPushNotifications = async (): Promise<PushSubscription | null> => {
  try {
    // Check if service worker is registered
    const registration = await navigator.serviceWorker.ready;
    
    // Get the server's public key
    const response = await apiClient.notifications.getVapidPublicKey();
    const vapidPublicKey = response.publicKey;
    
    // Convert the public key to the format expected by the browser
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
    
    // Subscribe to push notifications
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey
    });
    
    // Send the subscription to the server
    await sendSubscriptionToServer(subscription);
    
    // Send the application server key to the service worker
    if (registration.active) {
      registration.active.postMessage({
        type: 'SET_APPLICATION_SERVER_KEY',
        key: applicationServerKey
      });
    }
    
    return subscription;
  } catch (error) {
    console.error('Error subscribing to push notifications:', error);
    return null;
  }
};

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPushNotifications = async (): Promise<boolean> => {
  try {
    // Check if service worker is registered
    const registration = await navigator.serviceWorker.ready;
    
    // Get the current subscription
    const subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      return true; // Already unsubscribed
    }
    
    // Unsubscribe
    const success = await subscription.unsubscribe();
    
    if (success) {
      // Notify the server
      await deleteSubscriptionFromServer(subscription);
    }
    
    return success;
  } catch (error) {
    console.error('Error unsubscribing from push notifications:', error);
    return false;
  }
};

/**
 * Get the current push notification subscription
 */
export const getCurrentPushSubscription = async (): Promise<PushSubscription | null> => {
  try {
    // Check if service worker is registered
    const registration = await navigator.serviceWorker.ready;
    
    // Get the current subscription
    return await registration.pushManager.getSubscription();
  } catch (error) {
    console.error('Error getting push subscription:', error);
    return null;
  }
};

/**
 * Send the subscription to the server
 */
const sendSubscriptionToServer = async (subscription: PushSubscription): Promise<void> => {
  try {
    await apiClient.notifications.saveSubscription(subscription);
  } catch (error) {
    console.error('Error sending subscription to server:', error);
    throw error;
  }
};

/**
 * Delete the subscription from the server
 */
const deleteSubscriptionFromServer = async (subscription: PushSubscription): Promise<void> => {
  try {
    await apiClient.notifications.deleteSubscription(subscription.endpoint);
  } catch (error) {
    console.error('Error deleting subscription from server:', error);
    throw error;
  }
};

/**
 * Convert a base64 string to a Uint8Array
 * This is needed for the applicationServerKey
 */
export const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  
  return outputArray;
};